import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, LockOpen, Lock } from 'lucide-react'

interface Device {
  id: string
  name: string
  status: string
}

interface Props {
  devices: Device[]
  proxyUrl: string
  proxyToken: string
}

type ActionKey = `${string}-${'unlock' | 'lock'}`

export function DoorPanel({ devices, proxyUrl, proxyToken }: Props) {
  const [loading, setLoading] = useState<ActionKey | null>(null)

  async function handleAction(deviceId: string, action: 'unlock' | 'lock') {
    const key: ActionKey = `${deviceId}-${action}`
    setLoading(key)
    try {
      const res = await fetch(`${proxyUrl}/door/${deviceId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}` },
      })
      if (!res.ok) throw new Error()
      toast.success(action === 'unlock' ? 'Puerta abierta' : 'Puerta cerrada')
    } catch {
      toast.error(action === 'unlock' ? 'No se pudo abrir la puerta' : 'No se pudo cerrar la puerta')
    } finally {
      setLoading(null)
    }
  }

  if (devices.length === 0) {
    return (
      <p className="text-text-muted text-sm text-center py-4">
        No hay dispositivos configurados.{' '}
        <a href="/devices" className="text-accent hover:underline">Agregar dispositivo</a>
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {devices.map(device => {
        const online = device.status === 'online'
        const unlocking = loading === `${device.id}-unlock`
        const locking = loading === `${device.id}-lock`
        return (
          <div
            key={device.id}
            className={`flex items-center justify-between rounded-lg border px-4 py-3 ${online ? 'bg-surface border-border' : 'bg-surface border-border opacity-50'}`}
          >
            <div>
              <p className="text-text-primary text-sm font-medium">{device.name}</p>
              <p className={`text-xs flex items-center gap-1 mt-0.5 ${online ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {online ? 'Conectado' : 'Sin conexión'}
              </p>
            </div>
            <div className="flex gap-2">
              {online ? (
                <>
                  <button
                    disabled={loading !== null}
                    onClick={() => handleAction(device.id, 'unlock')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-accent text-background hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Abrir"
                  >
                    {unlocking ? <Loader2 className="w-3 h-3 animate-spin" /> : <LockOpen className="w-3 h-3" />}
                    Abrir
                  </button>
                  <button
                    disabled={loading !== null}
                    onClick={() => handleAction(device.id, 'lock')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Cerrar"
                  >
                    {locking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                    Cerrar
                  </button>
                </>
              ) : (
                <>
                  <button
                    disabled
                    aria-label="Dispositivo sin conexión"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-accent text-background disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <LockOpen className="w-3 h-3" />
                    <span aria-hidden="true">Abrir</span>
                  </button>
                  <button
                    disabled
                    aria-label="Dispositivo sin conexión (bloqueo)"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Lock className="w-3 h-3" />
                    <span aria-hidden="true">Cerrar</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
